import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerTicket, useAddCustomerComment, useUpdateCustomerTicket, useCustomerPortalCategories, useCustomerPortalLookups, useCustomerTicketActions, useResolveCustomerTicket, useReopenCustomerTicket, useCustomerPortalTicketArticles, customerPortalQueryKeys } from '../../hooks/useCustomerPortal';
import { useCustomerPortalSignalR } from '../../contexts/CustomerPortalSignalRContext';
import { showToast } from '../../lib/toast';
import { getErrorMessage } from '../../lib/apiErrors';
import { apiService } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileUpload } from '../../components/FileUpload';
import { AttachmentItem } from '../../components/AttachmentItem';
import {
  ArrowLeft,
  Send,
  AlertTriangle,
  CheckCircle,
  Edit,
  Save,
  X,
  User,
  Calendar,
  FolderOpen,
  RefreshCw,
  Signal,
  Ticket as TicketIcon,
  AlertCircle,
  Hourglass,
  XCircle,
  PauseCircle,
  ArrowDown,
  Minus,
  ArrowUp,
  CircleDot,
  Activity,
  BookOpen,
  GitMerge,
  Users
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useDateFormat } from '../../hooks/useDateFormat';
import { getStatusString, getPriorityString } from '../utils/ticketUtils';
import { TicketStatusColors, PriorityColors } from '../../types/tickets';
import { CustomerPortalTicketDetailSkeleton } from '../../components/LoadingSkeleton';
import { TicketProgressBar } from '../../components/TicketProgressBar';
import { CustomerPortalParticipants } from '../components/CustomerPortalParticipants';
import type { OrganizationInfo } from '../../lib/portal-loaders';
import { TreeSelect } from '../../components/tree-select/TreeSelect';
import { portalCategoryToOption } from '../../components/tree-select/treeOptions';
import { usePortalBranding } from '../../hooks/usePortalBranding';

interface CustomerPortalTicketDetailProps {
  params: { ticketId: string };
  organizationInfo?: OrganizationInfo;
  organizationId?: string | null;
}

export const CustomerPortalTicketDetail: React.FC<CustomerPortalTicketDetailProps> = ({ params, organizationInfo }) => {
  const navigate = useNavigate();
  const { ticketId } = params;
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  const { getThemedPrimaryColor, getThemedSecondaryColor, getContrastText, getThemedPrimaryWithOpacity } = usePortalBranding();
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editPriority, setEditPriority] = useState<number | null>(null);
  const [editCategoryId, setEditCategoryId] = useState<string>('');
  
  // Attachment state
  const [commentAttachments, setCommentAttachments] = useState<File[]>([]);
  const [fileUploadKey, setFileUploadKey] = useState("fileupload-0");
  
  const { data: ticket, isLoading, error } = useCustomerTicket(ticketId);
  const addCommentMutation = useAddCustomerComment();
  const updateTicketMutation = useUpdateCustomerTicket();
  const { data: categories = [] } = useCustomerPortalCategories();
  const { data: lookups } = useCustomerPortalLookups();
  const { data: ticketActions } = useCustomerTicketActions(ticketId);

  // A merged request is a closed stub - the conversation continues on another ticket. The server names that ticket
  // only when this customer can open it (they own it or were copied onto it); otherwise it may be someone else's.
  const isMerged = ticketActions?.isMerged ?? false;
  const mergedIntoTicketId = ticketActions?.mergedIntoTicketId ?? null;
  const mergedIntoTicketNumber = ticketActions?.mergedIntoTicketNumber ?? null;
  const { data: linkedArticles } = useCustomerPortalTicketArticles(ticketId);
  const resolveTicketMutation = useResolveCustomerTicket();
  const reopenTicketMutation = useReopenCustomerTicket();
  const { formatDate, formatDateTime } = useDateFormat();

  // SignalR real-time updates
  const queryClient = useQueryClient();
  const { signalR, isConnected } = useCustomerPortalSignalR();

  // Join/leave ticket group for real-time updates
  useEffect(() => {
    if (!ticket || !isConnected) return;

    signalR.joinTicket(ticket.id);

    return () => {
      signalR.leaveTicket(ticket.id);
    };
  }, [ticket, isConnected, signalR]);

  // Subscribe to SignalR events for real-time ticket detail updates
  useEffect(() => {
    if (!isConnected) return;

    const handleTicketUpdated = (data: { ticketId?: string; TicketId?: string }) => {
      const updateTicketId = data.ticketId || data.TicketId;
      if (updateTicketId === ticketId) {
        showToast.info('Ticket Updated', 'This ticket has been updated');
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: customerPortalQueryKeys.tickets.detail(ticketId),
          });
          // Invalidate actions to update Resolve/Reopen buttons
          queryClient.invalidateQueries({
            queryKey: [...customerPortalQueryKeys.tickets.detail(ticketId), 'actions'],
          });
          // Invalidate lists and stats for when user navigates back to dashboard
          queryClient.invalidateQueries({
            queryKey: customerPortalQueryKeys.tickets.lists(),
          });
          queryClient.invalidateQueries({
            queryKey: customerPortalQueryKeys.tickets.stats(),
          });
        }, 100);
      }
    };

    const handleCommentAdded = (data: { ticketId?: string; TicketId?: string; authorName?: string; AuthorName?: string }) => {
      const commentTicketId = data.ticketId || data.TicketId;
      if (commentTicketId === ticketId) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: customerPortalQueryKeys.tickets.detail(ticketId),
          });
        }, 100);

        const authorName = data.authorName || data.AuthorName;
        if (authorName) {
          showToast.info('New Comment', `${authorName} added a comment`);
        }
      }
    };

    const handleParticipantsChanged = (data: { ticketId?: string; changeType?: string; participant?: { customerName?: string } }) => {
      if (data.ticketId === ticketId) {
        // Invalidate ticket detail
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.detail(ticketId),
        });
        // Invalidate participants query (fetched separately by CustomerPortalParticipants component)
        queryClient.invalidateQueries({
          queryKey: ['customer-portal', 'tickets', ticketId, 'participants'],
        });

        if (data.changeType === 'added' && data.participant?.customerName) {
          showToast.info('Participant Added', `${data.participant.customerName} was added to this ticket`);
        } else if (data.changeType === 'removed' && data.participant?.customerName) {
          showToast.info('Participant Removed', `${data.participant.customerName} was removed from this ticket`);
        }
      }
    };

    const handleCommentAttachmentAdded = (data: { ticketId?: string; TicketId?: string }) => {
      const attachmentTicketId = data.ticketId || data.TicketId;
      if (attachmentTicketId === ticketId) {
        showToast.info('Attachment Added', 'An attachment was added to this ticket');
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.detail(ticketId),
        });
      }
    };

    // Register event handlers and store cleanup functions
    const cleanupTicketUpdated = signalR.on('TicketUpdated', handleTicketUpdated);
    const cleanupCommentAdded = signalR.on('CommentAdded', handleCommentAdded);
    const cleanupParticipantsChanged = signalR.on('ParticipantsChanged', handleParticipantsChanged);
    const cleanupCommentAttachmentAdded = signalR.on('CommentAttachmentAdded', handleCommentAttachmentAdded);

    // Cleanup - only removes this component's handlers, not global handlers
    return () => {
      cleanupTicketUpdated();
      cleanupCommentAdded();
      cleanupParticipantsChanged();
      cleanupCommentAttachmentAdded();
    };
  }, [isConnected, signalR, queryClient, ticketId]);

  // Remove automatic edit state initialization to prevent race conditions
  // Edit state will be initialized manually when entering edit mode

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsAddingComment(true);
    try {
      // Create the comment with attachments atomically
      await addCommentMutation.mutateAsync({
        ticketId,
        content: newComment.trim(),
        attachments: commentAttachments.length > 0 ? commentAttachments : undefined
      });

      // Clear form
      setNewComment('');
      setCommentAttachments([]);
      setFileUploadKey(`fileupload-${Date.now()}`);

    } catch {
      // The error will be shown via the mutation's toast notification
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleEditToggle = () => {
    if (!isEditing && ticket) {
      // Entering edit mode - safely initialize with current ticket values
      setEditPriority(ticket.priority);
      setEditCategoryId(ticket.category?.id || '');
    } else if (isEditing) {
      // Exiting edit mode - no need to reset state, just exit
      // State will be re-initialized on next edit entry
    }
    setIsEditing(!isEditing);
  };

  const handleSaveChanges = async () => {
    if (!ticket) return;

    try {
      // Always send both priority and categoryId to avoid null assignments. A category the customer can't change
      // (an internal one an agent chose) is sent back unchanged - the server ignores it either way.
      const updates = {
        priority: editPriority ?? ticket.priority,
        categoryId: ticket.canChangeCategory === false ? (ticket.category?.id ?? '') : editCategoryId
      };

      await updateTicketMutation.mutateAsync({ 
        ticketId, 
        updates 
      });
      
      setIsEditing(false);
    } catch {
      // The error will be shown via the mutation's toast notification
    }
  };

  const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
    try {
      const blob = await apiService.downloadCustomerPortalAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast.error('Download failed', getErrorMessage(error, `Could not download ${fileName}`));
    }
  };

  const handleGoBack = () => {
    navigate({ to: '..' });
  };

  const handleResolveTicket = async () => {
    if (resolveTicketMutation.isPending) return;

    try {
      await resolveTicketMutation.mutateAsync({
        ticketId
      });
    } catch {
      // Error handling is done in the mutation hook
    }
  };

  const handleReopenTicket = async () => {
    if (reopenTicketMutation.isPending) return;

    try {
      await reopenTicketMutation.mutateAsync({
        ticketId
      });
    } catch {
      // Error handling is done in the mutation hook
    }
  };

  const getStatusBadgeIcon = (status: number | string) => {
    const statusStr = getStatusString(status).toLowerCase();
    switch (statusStr) {
      case 'open': return <CircleDot className="h-3 w-3" />;
      case 'inprogress': return <Hourglass className="h-3 w-3" />;
      case 'pending': return <AlertCircle className="h-3 w-3" />;
      case 'resolved': return <CheckCircle className="h-3 w-3" />;
      case 'closed': return <XCircle className="h-3 w-3" />;
      case 'onhold': return <PauseCircle className="h-3 w-3" />;
      default: return <TicketIcon className="h-3 w-3" />;
    }
  };

  const getPriorityIcon = (priority: number | string) => {
    const priorityStr = getPriorityString(priority).toLowerCase();
    switch (priorityStr) {
      case 'low': return <ArrowDown className="h-3 w-3" />;
      case 'medium': return <Minus className="h-3 w-3" />;
      case 'high': return <ArrowUp className="h-3 w-3" />;
      case 'critical': return <AlertTriangle className="h-3 w-3" />;
      default: return <Minus className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return <CustomerPortalTicketDetailSkeleton />;
  }

  if (error || !ticket) {
    // Error state will be handled by UI display

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={handleGoBack} className="text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden min-[400px]:inline">Back to Tickets</span>
            <span className="min-[400px]:hidden">Back</span>
          </Button>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error 
              ? `Failed to load ticket details: ${error instanceof Error ? error.message : 'Unknown error'}` 
              : 'Ticket not found or you may not have permission to view this ticket.'} 
            Please try refreshing the page or go back to your tickets list.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // False when the customer is a participant on someone else's request: they read it and reply, the owner makes every other change
  const isOwner = ticket.isOwner !== false;
  const canComment = ticketActions?.canComment ?? (
    getStatusString(ticket.status) === 'Open' || getStatusString(ticket.status) === 'InProgress' ||
    getStatusString(ticket.status) === 'Pending' || getStatusString(ticket.status) === 'OnHold' ||
    getStatusString(ticket.status) === 'Resolved');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-gray-200 dark:border-gray-700 pb-3 sm:pb-4 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto flex-1 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleGoBack} className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tickets
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {ticket.subject}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Ticket #{ticket.ticketNumber}
            </p>
          </div>
        </div>
        <div className="flex flex-nowrap items-center gap-2 w-full sm:w-auto flex-shrink-0">
          {isEditing ? (
            <>
              <Button
                onClick={handleSaveChanges}
                disabled={updateTicketMutation.isPending}
                size="sm"
                className="transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                style={{
                  backgroundColor: getThemedPrimaryColor(),
                  color: getContrastText(getThemedPrimaryColor()),
                  borderColor: getThemedPrimaryColor(),
                  boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                }}
                onMouseEnter={(e) => {
                  if (!updateTicketMutation.isPending) {
                    e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                    e.currentTarget.style.borderColor = getThemedSecondaryColor();
                    e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                    e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                  e.currentTarget.style.borderColor = getThemedPrimaryColor();
                  e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                  e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                }}
              >
                <Save className="h-4 w-4 mr-2" />
                {updateTicketMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={handleEditToggle}
                size="sm"
                className="transition-all duration-200"
                style={{
                  borderColor: getThemedSecondaryColor(),
                  color: getThemedSecondaryColor(),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                  e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = getThemedSecondaryColor();
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            <>
              {/* Edit Ticket Button - the owner's, and only while the ticket is not resolved or closed */}
              {isOwner && !isMerged && (getStatusString(ticket.status) !== 'Resolved' && getStatusString(ticket.status) !== 'Closed') && (
                <Button
                  onClick={handleEditToggle}
                  size="sm"
                  variant="outline"
                  className="transition-all duration-200"
                  style={{
                    borderColor: getThemedSecondaryColor(),
                    color: getThemedSecondaryColor(),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                    e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = getThemedSecondaryColor();
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Ticket
                </Button>
              )}

              {/* Ticket Actions */}
              {ticketActions && (
                <>
                  {ticketActions.canResolve && (
                    <Button
                      onClick={handleResolveTicket}
                      disabled={resolveTicketMutation.isPending}
                      size="sm"
                      className="transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getThemedPrimaryColor(),
                        color: getContrastText(getThemedPrimaryColor()),
                        borderColor: getThemedPrimaryColor(),
                        boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                      }}
                      onMouseEnter={(e) => {
                        if (!resolveTicketMutation.isPending) {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.borderColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                          e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                        e.currentTarget.style.borderColor = getThemedPrimaryColor();
                        e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                        e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {resolveTicketMutation.isPending ? "Resolving..." : "Mark as Resolved"}
                    </Button>
                  )}
                  {ticketActions.canReopen && (
                    <Button
                      onClick={handleReopenTicket}
                      disabled={reopenTicketMutation.isPending}
                      size="sm"
                      variant="outline"
                      className="transition-all duration-200"
                      style={{
                        borderColor: getThemedSecondaryColor(),
                        color: getThemedSecondaryColor(),
                      }}
                      onMouseEnter={(e) => {
                        if (!reopenTicketMutation.isPending) {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = getThemedSecondaryColor();
                      }}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {reopenTicketMutation.isPending ? "Reopening..." : "Reopen Ticket"}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {!isOwner && (
        <Alert>
          <Users className="h-4 w-4" />
          <AlertDescription>
            You&apos;re a participant on this request from {ticket.customer?.fullName || 'another customer'}. You can follow
            the conversation and reply; they manage the request.
          </AlertDescription>
        </Alert>
      )}

      {/* Progress Bar */}
      <div className="flex justify-center py-2">
        <TicketProgressBar currentStatus={ticket.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Ticket Details */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Ticket Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Ticket Number & Subject
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-base sm:text-lg font-semibold text-foreground">
                    #{ticket.ticketNumber}
                  </span>
                  <span className="text-muted-foreground">-</span>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground">
                    {ticket.subject}
                  </h3>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Description
                </label>
                <div className="prose max-w-none dark:prose-invert">
                  <p className="text-foreground whitespace-pre-wrap">
                    {ticket.description}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Comments */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
              Conversation
            </h3>

            <div className="max-h-96 overflow-y-auto border rounded-md p-3 sm:p-4 bg-muted/20 scroll-smooth">
              {ticket.comments && ticket.comments.length > 0 ? (
                <div className="space-y-4">
                  {ticket.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`border-l-4 pl-4 py-3 rounded-r-md bg-muted/30 shadow-sm transition-shadow hover:shadow-md ${
                        comment.isFromUser
                          ? "border-green-400 dark:border-green-500"
                          : "border-green-400 dark:border-green-500"
                      }`}
                      style={comment.isFromUser ? {
                        borderLeftColor: getThemedPrimaryColor()
                      } : undefined}
                    >
                      <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm sm:text-base font-medium text-foreground">
                            {comment.authorName || 'System'}
                          </span>
                          <span className="text-xs sm:text-sm text-muted-foreground">
                            {comment.authorEmail || ''}
                          </span>
                          {comment.isSystemGenerated && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                              System
                            </span>
                          )}
                        </div>
                        <span className="text-xs sm:text-sm text-muted-foreground flex-shrink-0">
                          {formatDateTime(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">
                        {comment.content}
                      </p>
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-muted">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Attachments:
                          </p>
                          <div className="space-y-1">
                            {comment.attachments.map((attachment) => (
                              <AttachmentItem
                                key={attachment.id}
                                attachment={attachment}
                                variant="compact"
                                onDownload={handleDownloadAttachment}
                                siblings={comment.attachments}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <div className="text-muted-foreground mb-2">💬</div>
                    <p className="text-muted-foreground">No comments yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add the first comment below
                    </p>
                  </div>
                </div>
              )}

            </div>

            {/* Add Comment - the owner and participants, until the ticket is closed or merged */}
            {canComment && (
              <div className="mt-6 pt-6 border-t border-border">
                <h4 className="text-sm sm:text-base font-medium text-foreground mb-3">
                  Add Comment
                </h4>
                <div className="space-y-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={isEditing ? "Save or cancel your edits before adding a comment" : "Type your comment here..."}
                    disabled={isAddingComment || isEditing}
                  />

                  <div className="mt-3">
                    <FileUpload
                      key={fileUploadKey}
                      onFilesChange={setCommentAttachments}
                      maxFiles={5}
                      maxSize={10}
                      disabled={isEditing}
                    />
                  </div>

                  {isEditing && (
                    <p className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4" />
                      Comments are disabled while editing. Save or cancel your changes first.
                    </p>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isAddingComment || isEditing}
                      className="transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getThemedPrimaryColor(),
                        color: getContrastText(getThemedPrimaryColor()),
                        borderColor: getThemedPrimaryColor(),
                        boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                      }}
                      onMouseEnter={(e) => {
                        if (newComment.trim() && !isAddingComment) {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.borderColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                          e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                        e.currentTarget.style.borderColor = getThemedPrimaryColor();
                        e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                        e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {isAddingComment ? "Adding..." : "Add Comment"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {isMerged ? (
            <Alert>
              <GitMerge className="h-4 w-4" />
              <AlertDescription>
                {mergedIntoTicketId && organizationInfo ? (
                  <>
                    This request was combined with{' '}
                    <Link
                      to="/org/$orgSlug/portal/tickets/$ticketId"
                      params={{ orgSlug: organizationInfo.subdomain, ticketId: mergedIntoTicketId }}
                      className="font-medium underline underline-offset-2"
                    >
                      request #{mergedIntoTicketNumber}
                    </Link>
                    , which covers the same issue. Follow the conversation and reply there.
                  </>
                ) : (
                  'This request was combined with another request covering the same issue, so it can no longer be updated here.'
                )}
              </AlertDescription>
            </Alert>
          ) : getStatusString(ticket.status) === 'Closed' && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                This ticket has been closed. Comments are disabled.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Ticket Details */}
          <Card className="shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
              <CardTitle className="text-base sm:text-lg">Ticket Information</CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center">
                      <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Status
                    </label>
                  </div>
                  <div className="ml-10">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full w-fit ${TicketStatusColors[ticket.status]}`}
                    >
                      {getStatusBadgeIcon(ticket.status)}
                      <span>{getStatusString(ticket.status)}</span>
                    </span>
                  </div>
                </div>


                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-purple-50/50 dark:bg-purple-950/50 flex items-center justify-center">
                      <Signal className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Priority
                    </label>
                  </div>
                  <div className="ml-10">
                    {isEditing ? (
                      <Select
                        value={editPriority?.toString() || ''}
                        onValueChange={(value) => setEditPriority(parseInt(value))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          {lookups?.priorities.map((priority) => (
                            <SelectItem key={priority.value} value={priority.value.toString()}>
                              {priority.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${PriorityColors[ticket.priority]}`}
                      >
                        {getPriorityIcon(ticket.priority)}
                        {getPriorityString(ticket.priority)}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-green-50/50 dark:bg-green-950/50 flex items-center justify-center">
                      <FolderOpen className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Category
                    </label>
                  </div>
                  <div className="ml-10">
                    {isEditing && ticket.canChangeCategory !== false ? (
                      <TreeSelect
                        options={[
                          ...categories.map(portalCategoryToOption),
                          // The list only offers active categories; keep the ticket's current one if it was retired
                          ...(ticket.category && !categories.some((c) => c.id === ticket.category?.id)
                            ? [{ id: ticket.category.id, name: ticket.category.name, path: ticket.category.path || ticket.category.name, depth: 1 }]
                            : []),
                        ]}
                        value={editCategoryId || null}
                        onChange={(id) => setEditCategoryId(id ?? '')}
                        placeholder="Select category"
                        noneLabel="No category"
                        searchPlaceholder="Search categories..."
                        emptyMessage="No categories"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-white">
                        {ticket.category?.path || ticket.category?.name || 'No Category'}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Assigned To
                    </label>
                  </div>
                  <p className="ml-10 text-sm text-gray-900 dark:text-white">
                    {ticket.assignedTo?.fullName || 'Unassigned'}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-orange-50/50 dark:bg-orange-950/50 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Created
                    </label>
                  </div>
                  <p className="ml-10 text-sm text-gray-900 dark:text-white">
                    {formatDate(ticket.createdAt)}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50/50 dark:bg-indigo-950/50 flex items-center justify-center">
                      <RefreshCw className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Last Updated
                    </label>
                  </div>
                  <p className="ml-10 text-sm text-gray-900 dark:text-white">
                    {formatDateTime(ticket.updatedAt)}
                  </p>
                </div>

                {ticket.resolvedAt && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-lg bg-teal-50/50 dark:bg-teal-950/50 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                      </div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Resolved
                      </label>
                    </div>
                    <p className="ml-10 text-sm text-gray-900 dark:text-white">
                      {formatDate(ticket.resolvedAt)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Participants */}
          <CustomerPortalParticipants
            ticketId={ticketId}
            isTicketOwner={isOwner}
          />

          {/* Related Articles */}
          {linkedArticles && linkedArticles.count > 0 && (
            <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Related Articles
              </h3>
              <div className="space-y-2">
                {linkedArticles.articles.map((article) => (
                  <button
                    key={article.articleId}
                    onClick={() => navigate({ to: `../../knowledge-base/${article.articleId}` })}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-muted/50 transition-colors"
                  >
                    <p className="text-sm font-medium text-foreground hover:text-primary">
                      {article.title}
                    </p>
                    {article.summary && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {article.summary}
                      </p>
                    )}
                    {article.collectionName && (
                      <span className="inline-block text-[10px] text-muted-foreground mt-1.5 px-1.5 py-0.5 bg-muted rounded">
                        {article.collectionName}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
                Attachments
              </h3>
              <div className="space-y-2">
                {ticket.attachments.map((attachment) => (
                  <AttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    variant="full"
                    onDownload={handleDownloadAttachment}
                    siblings={ticket.attachments}
                  />
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};